import { ShiftCloseoutPaymentTotalInput } from '@/features/graphql/generated/graphql'
import { logger } from '@/features/shiftCloseouts/logger'
import { shiftCloseoutCreateMutation } from '@/features/shiftCloseouts/mutations'
import { ShiftCloseoutPreviewQuery, ShiftCloseoutsQuery } from '@/features/shiftCloseouts/queries'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import type { ApolloCache } from '@apollo/client'
import { useCallback, useRef } from 'react'

const cashTransactionCacheFields = [
    'cashRegisterBalance',
    'cashTransactionTotals',
    'cashTransactions',
] as const

export const invalidateCashTransactionCache = (cache: ApolloCache<unknown>): void => {
    cashTransactionCacheFields.forEach((fieldName) => {
        cache.evict({ fieldName, id: 'ROOT_QUERY' })
    })
    cache.gc()
}

type UseCreateShiftCloseoutResult = readonly [
    (reportedPaymentTotals: ShiftCloseoutPaymentTotalInput[]) => Promise<boolean>,
    boolean,
]

export const useCreateShiftCloseout = (): UseCreateShiftCloseoutResult => {
    const requestInFlightRef = useRef(false)
    const handleError = useHandleErrorWithToast(logger)
    const [createShiftCloseout, { loading }] = useMutation(shiftCloseoutCreateMutation, {
        onError: handleError,
        refetchQueries: [ShiftCloseoutPreviewQuery, ShiftCloseoutsQuery],
        update: (cache, { data }) => {
            if (!data?.shiftCloseoutCreate) return

            invalidateCashTransactionCache(cache)
        },
    })

    const handleCreateShiftCloseout = useCallback(
        async (reportedPaymentTotals: ShiftCloseoutPaymentTotalInput[]) => {
            if (requestInFlightRef.current) return false

            requestInFlightRef.current = true

            try {
                const { data } = await createShiftCloseout({ variables: { reportedPaymentTotals } })
                const shiftCloseout = data?.shiftCloseoutCreate

                if (!shiftCloseout) return false

                return true
            } finally {
                requestInFlightRef.current = false
            }
        },
        [createShiftCloseout]
    )

    return [handleCreateShiftCloseout, loading] as const
}
